import { useQuery } from '@apollo/client/react'
import { Card, Flex, Input } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import DocumentDialog from '../../components/DocumentDialog'
import { renderScanId } from '../../components/DocumentDialog/utils'
import DocumentListItem from '../../components/Elements/DocumentTableRow'
import { RemoveCustomerDocumentButton } from '../../components/RemoveCustomerDocumentButton'
import {
  DocumentSearchContainerIdDocument,
  DocumentSearchDocumentIdDocument,
} from '../../graph/generated/graphql'
import DocumentOwnerPreview from './DocumentOwnerPreview'

const ReAssignDocument = () => {
  const { t } = useTranslation()
  const [showDocumentDialog, setShowDocumentDialog] = useState(false)
  const [hasOpenedDocument, setHasOpenedDocument] = useState(false)

  // input accepts documentID OR containerID
  const [inputValue, setInputValue] = useState<string | undefined>(undefined)

  // search for documentId based on input value, in case user wrote the containerId
  const containerQuery = useQuery(DocumentSearchContainerIdDocument, {
    skip: !inputValue,
    variables: {
      containerId: inputValue,
    },
  })

  // use containerID match first, otherwise try inputvalue for documentID
  const documentId =
    // use documentId from container query if there is no error (otherwise may be outdated)
    (!containerQuery.error &&
      containerQuery.data?.containerDocument?.documentId) ||
    // use inputValue if no documentID yet available
    inputValue

  const documentQuery = useQuery(DocumentSearchDocumentIdDocument, {
    skip: !documentId,
    variables: { id: documentId },
  })

  const loading = documentQuery.loading || containerQuery.loading
  const document = documentQuery.error
    ? undefined
    : documentQuery.data?.document

  const onRemoveDocumentDone = () => {
    documentQuery.refetch()
    containerQuery.refetch()
  }

  return (
    <div>
      <Card>
        <Input.Search
          enterButton
          disabled={loading}
          placeholder={t('deleteDocuments.searchDocumentInputPlaceholder')}
          onSearch={setInputValue}
        />
      </Card>

      {!document && (
        <div style={{ marginTop: 16 }}>
          {t('deleteDocuments.noDocumentsSelected')}
        </div>
      )}
      {document && (
        <Flex vertical gap={16} style={{ marginTop: 16 }}>
          <DocumentListItem
            id={document.id}
            scanCenter={document.scanCenter}
            scanId={renderScanId(document)}
            stage={document.stage}
            tags={document.tags}
            rules={document.rules || []}
            from={document.senderMetadata?.name || ''}
            subject={document.subject}
            date={document.createdAt}
            paymentMetadata={document.paymentMetadata}
            isOpened={hasOpenedDocument}
            onClick={() => {
              setShowDocumentDialog(!showDocumentDialog)
              setHasOpenedDocument(true)
            }}
          />
          <DocumentOwnerPreview customer={document.customer} />
          {showDocumentDialog && (
            <DocumentDialog
              onClose={() => setShowDocumentDialog(false)}
              document={document}
              onRemoveDocumentDone={onRemoveDocumentDone}
            />
          )}
          <RemoveCustomerDocumentButton
            documentId={document.id}
            documentStage={document.stage}
            onDone={() => {
              setInputValue(undefined)
              onRemoveDocumentDone()
            }}
          />
        </Flex>
      )}
    </div>
  )
}

export default ReAssignDocument
